import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@familywallet/api-client';
import {
  type Account,
  accountNameSchema,
  isZeroMoney,
  sortCurrencies,
  UI_CURRENCIES,
} from '@familywallet/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardTitle } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { CurrencyAmount } from '../components/Money';
import { OpeningBalanceFields } from '../components/OpeningBalanceFields';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { accountKey, latestRateMap, useAccount, useCurrentRates } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { currencyName, formatAmount } from '../lib/money';
import { useIdempotencyKey } from '../lib/members';
import { checkOpening, openingFields, openingOutput } from '../lib/opening';
import { AccountBalance } from './AccountsPage';

const renameSchema = z.object({ name: accountNameSchema });

function useSaveAccount(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateLedger();
  return async (updated: Account) => {
    queryClient.setQueryData(accountKey(id), updated);
    await invalidate();
  };
}

function RenameCard({ account }: { account: Account }) {
  const { t } = useTranslation();
  const saved = useSaveAccount(account.id);
  const form = useForm<z.input<typeof renameSchema>, unknown, z.output<typeof renameSchema>>({
    resolver: zodResolver(renameSchema),
    values: { name: account.name },
  });
  const save = useMutation({
    mutationFn: (v: z.output<typeof renameSchema>) => api.accounts.update(account.id, v),
    onSuccess: saved,
  });
  return (
    <Card>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        <FormError error={save.error} />
        <Field label={t('accountDetail.rename')} error={form.formState.errors.name}>
          <Input {...form.register('name')} />
        </Field>
        <Button type="submit" variant="secondary" disabled={save.isPending}>
          {save.isSuccess && !form.formState.isDirty ? t('common.saved') : t('common.save')}
        </Button>
      </form>
    </Card>
  );
}

function PrivacyCard({ account }: { account: Account }) {
  const { t } = useTranslation();
  const saved = useSaveAccount(account.id);
  const toggle = useMutation({
    mutationFn: () => api.accounts.update(account.id, { isPrivate: !account.isPrivate }),
    onSuccess: saved,
  });
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('accountDetail.privacy')}</CardTitle>
      <FormError error={toggle.error} />
      <p className="text-sm text-muted">
        {account.isPrivate ? t('accounts.privateHint') : t('accountForm.privateHint')}
      </p>
      <Button variant="secondary" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
        {account.isPrivate ? t('accountDetail.makeVisible') : t('accountDetail.makePrivate')}
      </Button>
    </Card>
  );
}

/** Which currencies the account holds: remove one at zero, add another (with a starting amount). */
function CurrenciesCard({ account }: { account: Account }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const rates = useCurrentRates();
  const latestRates = useMemo(() => latestRateMap(rates.data), [rates.data]);
  const saved = useSaveAccount(account.id);
  const remove = useMutation({
    mutationFn: (currency: string) => api.accounts.removeCurrency(account.id, currency),
    onSuccess: saved,
  });
  const family = me.data?.family;
  if (!family || rates.isPending) return null;
  const held = account.balances.map((b) => b.currency);
  const addable = sortCurrencies(
    family.currencies.filter(
      (c) => !held.includes(c) && (UI_CURRENCIES as readonly string[]).includes(c),
    ),
    family.baseCurrency,
  );
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('accountDetail.currencies')}</CardTitle>
      <p className="text-sm text-muted">{t('accountDetail.currenciesHint')}</p>
      <FormError error={remove.error} />
      <ul className="flex flex-col gap-2">
        {account.balances.map((b) => (
          <li key={b.currency} className="flex min-h-11 items-center justify-between gap-3">
            <span className="flex flex-col">
              <span className="font-medium">{currencyName(b.currency, i18n.language)}</span>
              <CurrencyAmount
                amount={b.balance}
                currency={b.currency}
                owes={account.kind === 'card'}
              />
            </span>
            {held.length > 1 && (
              <Button
                variant="ghost"
                disabled={remove.isPending || !isZeroMoney(b.balance)}
                title={isZeroMoney(b.balance) ? undefined : t('accountDetail.removeNeedsZero')}
                onClick={() => {
                  if (window.confirm(t('accountDetail.removeConfirm', { currency: b.currency })))
                    remove.mutate(b.currency);
                }}
              >
                {t('accountDetail.removeCurrency', { currency: b.currency })}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {held.length > 1 && account.balances.some((b) => !isZeroMoney(b.balance)) && (
        <p className="text-xs text-muted">{t('accountDetail.removeNeedsZero')}</p>
      )}
      {addable.length > 0 && (
        <AddCurrencyForm
          key={addable.join()}
          account={account}
          currencies={addable}
          baseCurrency={family.baseCurrency}
          latestRates={latestRates}
        />
      )}
    </Card>
  );
}

function addCurrencySchema(baseCurrency: string, latestRates: Record<string, string>) {
  return z
    .object(openingFields)
    .superRefine((v, ctx) => checkOpening(v, ctx, baseCurrency, latestRates))
    .transform((v) => openingOutput(v, baseCurrency, latestRates));
}

function AddCurrencyForm({
  account,
  currencies,
  baseCurrency,
  latestRates,
}: {
  account: Account;
  currencies: string[];
  baseCurrency: string;
  latestRates: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const saved = useSaveAccount(account.id);
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const schema = useMemo(
    () => addCurrencySchema(baseCurrency, latestRates),
    [baseCurrency, latestRates],
  );
  type FormIn = z.input<typeof schema>;
  type FormOut = z.output<typeof schema>;
  const form = useForm<FormIn, unknown, FormOut>({
    resolver: zodResolver(schema),
    defaultValues: { currency: currencies[0] ?? baseCurrency, openingBalance: '', openingRate: '' },
  });
  const currency = useWatch({ control: form.control, name: 'currency' });
  const openingBalance = useWatch({ control: form.control, name: 'openingBalance' });
  const openingRate = useWatch({ control: form.control, name: 'openingRate' });
  const add = useMutation({
    mutationFn: (v: FormOut) => api.accounts.addCurrency(account.id, v, { idempotencyKey }),
    onSuccess: async (updated) => {
      renewKey();
      await saved(updated);
    },
  });
  return (
    <form
      noValidate
      className="flex flex-col gap-3 border-t border-line pt-3"
      onSubmit={form.handleSubmit((v) => add.mutate(v))}
    >
      <FormError error={add.error} />
      <Field label={t('accountDetail.addCurrency')}>
        <Select {...form.register('currency')}>
          {currencies.map((c) => (
            <option key={c} value={c}>
              {t('currencies.option', { code: c, name: currencyName(c, i18n.language) })}
            </option>
          ))}
        </Select>
      </Field>
      <OpeningBalanceFields
        label={
          account.kind === 'card'
            ? t('accountForm.openingBalanceCard')
            : t('accountForm.openingBalance')
        }
        currency={currency}
        baseCurrency={baseCurrency}
        latestRates={latestRates}
        amountValue={openingBalance}
        rateValue={openingRate}
        amountField={form.register('openingBalance')}
        rateField={form.register('openingRate')}
        amountError={form.formState.errors.openingBalance}
        rateError={form.formState.errors.openingRate}
      />
      <Button type="submit" variant="secondary" disabled={add.isPending}>
        {t('accountDetail.addCurrencySubmit', { currency })}
      </Button>
    </form>
  );
}

function ArchiveCard({ account }: { account: Account }) {
  const { t, i18n } = useTranslation();
  const saved = useSaveAccount(account.id);
  const change = useMutation({
    mutationFn: (confirmNonZeroBalance: boolean) =>
      account.archivedAt
        ? api.accounts.unarchive(account.id)
        : api.accounts.archive(account.id, { confirmNonZeroBalance }),
    onSuccess: saved,
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'account.balance_not_zero') {
        const amount = account.balances
          .filter((b) => !isZeroMoney(b.balance))
          .map((b) => formatAmount(b.balance, b.currency, i18n.language))
          .join(' + ');
        if (window.confirm(t('accountDetail.archiveConfirm', { amount }))) change.mutate(true);
      }
    },
  });
  const needsConfirm =
    change.error instanceof ApiError && change.error.code === 'account.balance_not_zero';
  return (
    <Card className="flex flex-col gap-3">
      {!needsConfirm && <FormError error={change.error} />}
      <p className="text-sm text-muted">{t('accountDetail.archiveHint')}</p>
      <Button variant="secondary" onClick={() => change.mutate(false)} disabled={change.isPending}>
        {account.archivedAt ? t('accountDetail.unarchive') : t('accountDetail.archive')}
      </Button>
    </Card>
  );
}

export function AccountDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const me = useMe();
  const account = useAccount(id);
  const canPrivate = useCan('accounts.private');

  if (account.isPending) return <p role="status">{t('app.loading')}</p>;
  if (account.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('accounts.title')} back="/accounts" />
        <FormError error={account.error} />
      </div>
    );
  }
  const a = account.data;
  const mine = a.ownerMemberId === me.data?.member.id;
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={a.name} back="/accounts" />
      <Card className="flex flex-col gap-1">
        <span className="text-sm text-muted">{t('accountDetail.balance')}</span>
        <span className="text-3xl font-semibold">
          <AccountBalance account={a} align="start" />
        </span>
        <span className="text-sm text-muted">
          {[
            t(`accountKinds.${a.kind}`),
            a.ownerMemberId
              ? `${t('accountDetail.owner')}: ${a.ownerDisplayName ?? ''}`
              : t('accountDetail.shared'),
            ...(a.isPrivate ? [t('accounts.private')] : []),
            ...(a.archivedAt
              ? [`${t('accountDetail.archivedOn')} ${date.format(new Date(a.archivedAt))}`]
              : []),
          ].join(' · ')}
        </span>
      </Card>
      {a.canManage && <RenameCard account={a} />}
      {a.canManage && !a.archivedAt && <CurrenciesCard account={a} />}
      {mine && (canPrivate || a.isPrivate) && <PrivacyCard account={a} />}
      {a.canManage && <ArchiveCard account={a} />}
    </div>
  );
}
